/**
 * MemberDetailsPage Component
 * Full detail view of a single member with breadcrumbs
 */

import { useState, useMemo } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/layout/Layout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Breadcrumbs, getRootBreadcrumb, type BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
import { MemberFormModal } from '../components/members/MemberFormModal';
import { RemoveMemberModal } from '../components/members/RemoveMemberModal';
import { MemberHrPanel } from '../components/members/MemberHrPanel';
import { membersService } from '../services/members.service';
import { handleApiError } from '../lib/axios';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useMemberImage } from '../hooks/useMemberImage';
import { formatDate } from '../lib/utils';
import type { Role } from '../types';

export const MemberDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const backLabel = (location.state as { backLabel?: string } | null)?.backLabel;
  const queryClient = useQueryClient();

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [hasLinkedUser, setHasActiveUser] = useState(false);
  const [linkedUserRole, setLinkedUserRole] = useState<Role | null>(null);

  // Fetch member data
  const {
    data: member,
    isLoading,
    error,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['member', id],
    queryFn: () => membersService.getById(Number(id)),
    enabled: !!id,
  });

  // Load profile image with automatic retry on blob URL failures
  const { imageUrl: imageBlobUrl, isLoading: imageLoading, handleImageError } = useMemberImage(
    member?.id ?? null,
    member?.has_profile_picture ?? false,
    { refreshTrigger: dataUpdatedAt }
  );

  // Teams this member leads — comes from the member detail response now (sorted biggest-first by the backend).
  const teamsLed = useMemo(() => member?.led_teams ?? [], [member]);

  // Build breadcrumb hierarchy with fallbacks for members without a team
  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    if (!member) return [];
    const items: BreadcrumbItem[] = [getRootBreadcrumb()];

    // 1. Member is assigned to a team — current behavior
    if (member.team_id && member.team_name) {
      if (member.department_id && member.department_name) {
        items.push({ label: member.department_name, to: `/departments/${member.department_id}` });
      }
      items.push({ label: member.team_name, to: `/teams/${member.team_id}` });
      return items;
    }

    // 2. No team, but directs a department (sorted biggest-first by the backend)
    const directedDept = member.directed_departments?.[0];
    if (directedDept) {
      items.push({ label: directedDept.name, to: `/departments/${directedDept.id}` });
      return items;
    }

    // 3. No team, not a director, but manages a department
    const managedDept = member.managed_departments?.[0];
    if (managedDept) {
      items.push({ label: managedDept.name, to: `/departments/${managedDept.id}` });
      return items;
    }

    // 4. No team, no director/manager role, but leads a team — pick the biggest active team
    const activeLed = teamsLed.filter((t) => t.is_active !== 0);
    if (activeLed.length > 0) {
      const biggestLed = activeLed[0]; // backend already sorts biggest-first
      if (biggestLed.department_id && biggestLed.department_name) {
        items.push({
          label: biggestLed.department_name,
          to: `/departments/${biggestLed.department_id}`,
        });
      }
      items.push({ label: biggestLed.name, to: `/teams/${biggestLed.id}` });
      return items;
    }

    // 5. Nothing to show — Home only
    return items;
  }, [member, teamsLed]);

  // Check linked user mutation
  const checkLinkedUserMutation = useMutation({
    mutationFn: () => membersService.checkLinkedUser(Number(id)),
    onSuccess: (data) => {
      setHasActiveUser(data.has_linked_user);
      setLinkedUserRole(data.user_role ?? null);
      setIsRemoveModalOpen(true);
    },
    onError: (error: Error) => {
      toast(handleApiError(error) || 'Failed to check linked user', 'error');
    },
  });

  // Remove mutation (soft-delete)
  const removeMutation = useMutation({
    mutationFn: () => membersService.delete(Number(id), hasLinkedUser),
    onSuccess: () => {
      toast('Member removed successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['members'] });
      navigate('/members');
    },
    onError: (error: Error) => {
      toast(handleApiError(error) || 'Failed to remove member', 'error');
    },
  });

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: () => membersService.restore(Number(id)),
    onSuccess: () => {
      toast('Member restored successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['member', id] });
    },
    onError: (error: Error) => {
      toast(handleApiError(error) || 'Failed to restore member', 'error');
    },
  });

  const handleRemoveClick = () => {
    checkLinkedUserMutation.mutate();
  };

  const handleRemove = () => {
    setIsRemoveModalOpen(false);
    removeMutation.mutate();
  };

  const handleRestore = () => {
    restoreMutation.mutate();
  };

  // Calculate years in team
  const calculateTenure = (joinDate: string) => {
    const start = new Date(joinDate);
    const now = new Date();
    const years = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    const totalYears = Math.floor(years);
    const months = Math.floor((years - totalYears) * 12);

    if (totalYears === 0 && months === 0) return 'Less than 1 month';
    if (totalYears === 0) return `${months} month${months !== 1 ? 's' : ''}`;
    if (months === 0) return `${totalYears} year${totalYears !== 1 ? 's' : ''}`;
    return `${totalYears} year${totalYears !== 1 ? 's' : ''}, ${months} month${
      months !== 1 ? 's' : ''
    }`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" text="Loading member details..." />
        </div>
      </Layout>
    );
  }

  if (error || !member) {
    return (
      <Layout>
        <div className="text-center py-12">
          <svg
            className="mx-auto h-16 w-16 text-red-500 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="text-lg font-medium text-heading mb-2">Member Not Found</h3>
          <p className="text-meta mb-4">
            The member you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/members')}>
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Members
          </Button>
        </div>
      </Layout>
    );
  }


  const isFutureJoinDate = new Date(member.date_joined) > new Date();
  const experience = isFutureJoinDate ? null : calculateTenure(member.date_joined);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center justify-between">
          <Breadcrumbs items={breadcrumbItems} />
          <Button variant="secondary" className="hidden lg:inline-flex" onClick={() => backLabel ? navigate(-1) : navigate('/members')}>
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to {backLabel || 'Members'}
          </Button>
        </div>
        <div className="bg-surface shadow overflow-hidden sm:rounded-lg">
          {/* Member Header */}
          <div className="px-4 py-5 sm:px-6 border-b border-[var(--border-default)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Profile Picture */}
                <div className="flex-shrink-0">
                  {member.has_profile_picture && imageBlobUrl ? (
                    <div className="w-24 h-24 rounded-full bg-surface-secondary flex items-center justify-center overflow-hidden">
                      {imageLoading ? (
                        <svg
                          className="animate-spin h-8 w-8 text-blue-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      ) : (
                        <img
                          src={imageBlobUrl}
                          alt={`${member.first_name} ${member.last_name}`}
                          className="w-full h-full object-cover"
                          onError={handleImageError}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-2xl font-bold">
                      {member.first_name[0]}
                      {member.last_name[0]}
                    </div>
                  )}
                </div>
                {/* Name and Status */}
                <div>
                  <h2 className="text-2xl font-bold text-heading">
                    {member.first_name} {member.last_name}
                  </h2>
                  <div className="mt-1 flex items-center space-x-2">
                    {member.removed_at ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-neutral text-status-neutral">
                        <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Removed
                      </span>
                    ) : member.is_user ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-success text-status-success">
                        <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        User
                      </span>
                    ) : !member.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-danger text-status-danger">
                        <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Inactive
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              {/* Action Buttons — visible at md+ only; mobile copy lives below the bio/children section */}
              {hasAdminAccess(user?.role) && (
                <div className="hidden md:flex items-center space-x-9">
                  {member.removed_at ? (
                    <Button
                      variant="primary"
                      onClick={handleRestore}
                      isLoading={restoreMutation.isPending}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Restore
                    </Button>
                  ) : (
                    <>
                      <Button variant="secondary" onClick={() => setIsEditModalOpen(true)}>
                        <svg
                          className="h-5 w-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        onClick={handleRemoveClick}
                        isLoading={checkLinkedUserMutation.isPending}
                      >
                        <svg
                          className="h-5 w-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Member Information */}
          <div className="px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-meta flex items-center">
                  <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email
                </dt>
                <dd className="mt-1 text-sm text-heading">
                  <a href={`mailto:${member.email}`} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    {member.email}
                  </a>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-meta flex items-center">
                  <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Team
                </dt>
                <dd className="mt-1 text-sm text-heading">
                  {member.team_id ? (
                    <Link to={`/teams/${member.team_id}`} state={{ backLabel: 'Member' }} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      {member.team_name || `Team ${member.team_id}`}
                    </Link>
                  ) : (
                    'No Team Assigned'
                  )}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-meta flex items-center">
                  <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Gender
                </dt>
                <dd className="mt-1 text-sm text-heading capitalize">
                  {member.gender || 'Not specified'}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-meta flex items-center">
                  <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Job Title
                </dt>
                <dd className="mt-1 text-sm text-heading">{member.position || 'No job title specified'}</dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-meta flex items-center">
                  <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Date of Birth
                </dt>
                <dd className="mt-1 text-sm text-heading">
                  {member.date_of_birth ? formatDate(member.date_of_birth) : 'Not provided'}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-meta flex items-center">
                  <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {isFutureJoinDate ? 'Joining Date' : 'Date Joined Organization'}
                </dt>
                <dd className="mt-1 text-sm text-heading">
                  {formatDate(member.date_joined)}
                </dd>
              </div>

              {experience && (
                <div>
                  <dt className="text-sm font-medium text-meta flex items-center">
                    <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Experience
                  </dt>
                  <dd className="mt-1 text-sm text-heading">{experience}</dd>
                </div>
              )}

              {teamsLed.length > 0 && (
                <div>
                  <dt className="text-sm font-medium text-meta flex items-center">
                    <svg className="h-4 w-4 text-meta mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Leads Teams
                  </dt>
                  <dd className="mt-1 text-sm text-heading">
                    {teamsLed.map((team, index) => (
                      <span key={team.id}>
                        <Link to={`/teams/${team.id}`} state={{ backLabel: 'Member' }} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                          {team.name}
                        </Link>
                        {index < teamsLed.length - 1 && ', '}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
            </dl>

            {/* Biography & Children Section */}
            <div className="mt-6 border-t border-[var(--border-default)] pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Biography */}
                <div className="sm:pr-6">
                  <h3 className="text-sm font-medium text-meta flex items-center mb-3">
                    <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Biography
                  </h3>
                  {member.biography ? (
                    <div className="text-sm text-heading prose max-w-none whitespace-pre-wrap">
                      {member.biography}
                    </div>
                  ) : (
                    <p className="text-sm text-meta italic">No biography available</p>
                  )}
                </div>

                {/* Children */}
                <div>
                  <h3 className="text-sm font-medium text-meta flex items-center mb-3">
                    <svg className="h-4 w-4 text-meta mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Children
                  </h3>
                  <p className="text-sm text-meta italic">No children added</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* HR Records — separate container, HR managers only (data is also API-gated) */}
        {user?.role === 'hr' && <MemberHrPanel memberId={member.id} />}

        {/* Mobile-only Action Buttons (below 768px) — outside the card so they sit on the page background */}
        {hasAdminAccess(user?.role) && (
          <div className="md:hidden flex items-center space-x-9">
            {member.removed_at ? (
              <Button
                variant="primary"
                onClick={handleRestore}
                isLoading={restoreMutation.isPending}
              >
                <svg
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Restore
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setIsEditModalOpen(true)}>
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit
                </Button>
                <Button
                  variant="danger"
                  onClick={handleRemoveClick}
                  isLoading={checkLinkedUserMutation.isPending}
                >
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Remove
                </Button>
              </>
            )}
          </div>
        )}

        {/* Modals */}
        {member && (
          <>
            <MemberFormModal
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              mode="edit"
              memberId={member.id}
            />
            <RemoveMemberModal
              isOpen={isRemoveModalOpen}
              onClose={() => setIsRemoveModalOpen(false)}
              onConfirm={handleRemove}
              memberName={`${member.first_name} ${member.last_name}`}
              hasLinkedUser={hasLinkedUser}
        linkedUserRole={linkedUserRole}
              isRemoving={removeMutation.isPending}
            />
          </>
        )}
      </div>
    </Layout>
  );
};
